import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { TrafficLight } from '@/components/traffic-light';
import { DELAY_STATUS } from '@/lib/constants';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { DelayStatus } from '@/types';
import { formatDuration } from '@/utils/format';
import { formatDateTimeForUser } from '@/utils/format/date-time';
import { tv } from '@/utils/styles';

export interface ShipmentEtaTooltipProps {
  status?: DelayStatus;
  delay?: ShipmentModel['delay'];
  eta?: ShipmentModel['eta'];
  originalEta?: ShipmentModel['originalEta'];
}

const ShipmentEtaTooltip = ({ status, delay, eta, originalEta }: ShipmentEtaTooltipProps) => {
  const isOnTime = status === DELAY_STATUS.ONTIME;
  const isDelayed = status === DELAY_STATUS.DELAYED;
  const delayDuration = formatDuration(Math.abs((delay || 0) * 60));

  let title = 'No delay detected';
  let description = 'This shipment appears to be on time.';

  if (status === DELAY_STATUS.DELAYED) {
    title = 'Delay detected';
    description = `A change to the original ETA was detected and this shipment is now expected to arrive ${delayDuration} late.`;
  }

  if (status === DELAY_STATUS.AHEAD) {
    title = 'Arriving early';
    description = `A change to the original ETA was detected and shipment appears to be arriving ${delayDuration} earlier than expected.`;
  }

  const { base, header, headerText, body, definitionList, definitionTitle, definitionDescription } = styles();

  return (
    <div className={base()}>
      <header className={header()}>
        {isDelayed && <TrafficLight color="red" showAlertIcon />}
        <Heading variant="h5" className={headerText()}>
          {title}
        </Heading>
      </header>
      <div className={body()}>
        <Text className="mb-2 text-sm opacity-80">{description}</Text>
        <dl className={definitionList()}>
          <dt className={definitionTitle()}>Latest ETA</dt>
          <dd className={definitionDescription()}>{eta ? formatDateTimeForUser(eta) : '-'}</dd>
        </dl>
        {!isOnTime && (
          <>
            <dl className={definitionList()}>
              <dt className={definitionTitle()}>Original ETA</dt>
              <dd className={definitionDescription()}>{originalEta ? formatDateTimeForUser(originalEta) : '-'}</dd>
            </dl>
            <dl className={definitionList()}>
              <dt className={definitionTitle()}>ETA difference</dt>
              <dd className={definitionDescription()}>
                {isDelayed ? '+' : ''} {delayDuration}
              </dd>
            </dl>
          </>
        )}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'w-64 p-2 pb-3 text-left',
    header: 'mb-2 flex items-center border-b border-grey-700 pb-2',
    headerText: 'm-0 font-medium',
    body: 'leading-normal',
    definitionList: 'm-0 mb-1 grid grid-cols-2 text-sm last-of-type:mb-0',
    definitionTitle: 'm-0 flex min-w-44 items-center p-0 font-semiBold',
    definitionDescription: 'justify-right m-0 flex items-center p-0 text-right',
  },
});

export { ShipmentEtaTooltip };
